import React, { useState } from "react";
import { Row, Col, Input, Label, FormFeedback, Button } from "reactstrap";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { createSkill, deleteskill } from "slices/Course/thunk";
import { AppDispatch } from "store";

type SkillItem = string | { id?: number; skill_name: string };

const normalizeSkill = (s: SkillItem): { id?: number; skill_name: string } =>
  typeof s === "string" ? { skill_name: s } : { id: s.id, skill_name: s.skill_name || "" };

interface SkillsFormProps {
  validation: any;
  courseId: number;
}

const SkillsForm: React.FC<SkillsFormProps> = ({ validation, courseId }) => {
  const [skillInput, setSkillInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const dispatch = useDispatch<AppDispatch>();

  const rawSkills: SkillItem[] = validation.values.skills ?? [];
  const skillsList = rawSkills.map(normalizeSkill);

  const addSkill = async () => {
    const skillName = skillInput.trim();
    if (!skillName) return;

    const courseIdNum = Number(courseId);
    if (!courseIdNum || Number.isNaN(courseIdNum)) {
      toast.error("Please save the Basic Info first !");
      return;
    }

    try {
      setSaving(true);
      const payload = { course_id: courseIdNum, skill_name: skillName };
      const result = (await dispatch(createSkill(payload))) as {
        success: boolean;
        message?: string;
        data?: number;
      };

      if (result?.success) {
        const newItem =
          result.data != null
            ? { id: result.data, skill_name: skillName }
            : { skill_name: skillName };
        const updatedSkills = [...rawSkills, newItem];
        validation.setFieldValue("skills", updatedSkills);
        setSkillInput("");
        toast.success("Skill added successfully!");
      } else {
        toast.error(result?.message || "Failed to add skill");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to add skill");
    } finally {
      setSaving(false);
    }
  };

  const deleteSkill = async (index: number) => {
    const item = skillsList[index];
    const courseIdNum = Number(courseId);
    const id = item?.id;

    const updatedSkills = rawSkills.filter((_: SkillItem, i: number) => i !== index);
    validation.setFieldValue("skills", updatedSkills);

    if (id != null && !Number.isNaN(courseIdNum)) {
      try {
        setDeletingId(id);
        await dispatch(deleteskill(id, courseIdNum));
        toast.success("Skill has been deleted successfully");
      } catch (err) {
        validation.setFieldValue("skills", rawSkills);
        toast.error("Failed to delete skill");
      } finally {
        setDeletingId(null);
      }
    }
  };

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <Row className="g-3 mt-2">
        <Col md={12}>
          <Label>Add Skill <span className="text-danger">*</span></Label>
          <div className="d-flex align-items-center border rounded p-1">
            <Input
              type="text"
              name="skill"
              placeholder="Write to add"
              value={skillInput}
              onChange={(e) => setSkillInput(e.target.value)}
              invalid={!!(validation.touched.skills && validation.errors.skills)}
              className="border-0"
              disabled={saving}
            />
            <Button color="success" disabled={!skillInput.trim() || saving} onClick={addSkill}>
              {saving ? (
                <i className="fa fa-spinner fa-spin" style={{ fontSize: "1rem" }} aria-hidden />
              ) : (
                <i className="ri-add-line" aria-hidden />
              )}
            </Button>
          </div>
          <FormFeedback>{validation.errors.skills}</FormFeedback>
        </Col>
      </Row>

      <Row className="mt-3">
        <Col md={12}>
          <div className="d-flex flex-wrap gap-2">
            {skillsList.map((item, index) => (
              <div
                key={item.id ?? index}
                className="position-relative px-3 py-1 bg-light border rounded-pill"
                style={{ minWidth: "100px" }}
              >
                {item.skill_name}
                <i
                  className="ri-close-line text-danger"
                  style={{
                    cursor: deletingId === item.id ? "wait" : "pointer",
                    position: "absolute",
                    top: "-6px",
                    right: "-6px",
                    fontSize: "18px",
                    background: "white",
                    borderRadius: "50%",
                  }}
                  onClick={() => (deletingId != null ? undefined : deleteSkill(index))}
                  aria-hidden
                />
                {deletingId === item.id && (
                  <span className="position-absolute top-0 start-50 translate-middle">
                    <i className="fa fa-spinner fa-spin small" />
                  </span>
                )}
              </div>
            ))}
          </div>
        </Col>
      </Row>
    </form>
  );
};

export default SkillsForm;
